import React, { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import dynamic from "next/dynamic";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useRouter } from "next/navigation";
import { ReCAPTCHA } from "@/lib/vendor";
import { Formik, Form, ErrorMessage } from "formik";

import apiService from "@/common/services/apiService";
import { acceptedFileTypes, PHONE_REGEX } from "@/utils/common";
import { hideFormLoader, showFormLoader } from "@/store/loaderSlice";

const ServiceSelect = dynamic(
  () => import("../../../Home/Quote/QuoteForm/ServiceSelect"),
);
const FileUploader = dynamic(
  () => import("../../../Home/Quote/QuoteForm/FileUploader"),
);
const FormInput = dynamic(
  () => import("../../../Home/Quote/QuoteForm/FormInput"),
);
const Button = dynamic(() => import("@/Components/Button"));

interface QuoteFormValues {
  email: string;
  resume: string;
  job_role: string;
  first_name: string;
  last_name: string;
  contact_no: string;
  current_company: string;
  current_job_role: string;
  total_experience: string;
}

const initials: QuoteFormValues = {
  email: "",
  resume: "",
  job_role: "",
  last_name: "",
  first_name: "",
  contact_no: "",
  current_company: "",
  current_job_role: "",
  total_experience: "Fresher",
};

// Custom error component for better accessibility
const ErrorMessageComponent = (props: any) => (
  <div className="text-red-500 font-medium text-xs mt-2" role="alert">
    {props.children}
  </div>
);
const years = Array.from({ length: 10 }, (v, k) => k + 1 + "+ Year");

interface ApplyFormProps {
  data?: any;
  job?: any;
}

const ApplyForm: React.FC<ApplyFormProps> = ({ job, data }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const [isLoading, setIsLoading] = useState(false);
  const sitekey = process.env.NEXT_PUBLIC_RECAPTCHA_V3;
  const [role, setRole] = useState(job?.position ?? "");
  const [selectedFiles, setSelectedFiles] = useState<any[]>([]);
  const [initialValues, setInitialValues] = useState<any>(initials);

  useEffect(() => {
    if (job?.position) {
      setRole(job?.position);
      initialValues.job_role = job?.position;
      setInitialValues(initialValues);
    }
  }, [job, initialValues]);

  const validationSchema = yup
    .object({
      first_name: yup.string().required("First name is required."),
      last_name: yup.string().required("Last name is required."),
      contact_no: yup
        .string()
        .required("Contact number is required.")
        .min(10, "Invalid phone number")
        .max(13, "Invalid phone number")
        .matches(PHONE_REGEX, "Invalid phone number"),
      email: yup
        .string()
        .email("Email is not valid.")
        .required("Email is required."),
      job_role: yup.string(), //.required("Position applying is required."),
      total_experience: yup.string(), //.required("Total experience is required."),
      resume: yup
        .mixed()
        .test("length", "Resume is required.", () => {
          if (!selectedFiles || selectedFiles.length == 0) {
            return false;
          } else {
            return true;
          }
        })
        .test(
          "type",
          "Only the following formats are accepted: .pdf and .doc",
          () => {
            const value1 = selectedFiles;
            const array = Array(value1.length).fill(null);
            if (selectedFiles && selectedFiles.length > 0) {
              const isValid = array.map((element, index) => {
                return acceptedFileTypes.includes(value1[index].type);
              });
              return !isValid.includes(false);
            } else {
              return true;
            }
          },
        ),
      current_job_role: yup.string().nullable(),
      current_company: yup.string().nullable(),
    })
    .required();

  const validateCaptchaV3 = () => {
    return new Promise((resolve) => {
      reCaptchaRef?.current?.executeAsync()?.then(async (value: unknown) => {
        resolve(value);
        // const captchaRes = await axios.post(`/api/validateCaptchaV3`, {token: value});
        // if (captchaRes.status === 200 && captchaRes.data.success) resolve(true); else reject(false);
      });
    });
  };

  const handleSubmit = async (values: any) => {
    try {
      setIsLoading(true);
      dispatch(showFormLoader());
      const isHuman = await validateCaptchaV3();
      if (!isHuman) return false;
      if (values.job_role == "") values.job_role = job?.position;
      const formData = { ...values };
      const form_data = new FormData();
      delete formData.resume;
      for (const key in formData) {
        form_data.append(key, formData[key]);
      }
      if (selectedFiles?.length) {
        selectedFiles?.forEach((file) => {
          form_data.append(`resume`, file);
        });
      }
      form_data.append("captcha_token_v3", isHuman as string);
      form_data.append("page_link", window.location.href);
      form_data.append("page_title", document.title);
      setSelectedFiles([]);
      const lst: any = await apiService.multipart_post("apply-now", form_data);
      dispatch(hideFormLoader());

      if (lst?.data?.success) router.push("/thank-you-for-applying-job");
      else toast.error(lst?.data?.message ?? "Something went wrong");
    } catch (error) {
      console.log("[ApplyForm] handleSubmit ~ error:", error);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 5000);
    }
  };

  return (
    <div className="bg-[url(/images/apply-form-bg.png)]  bg-no-repeat bg-cover bg-center h-full w-full ">
      <Formik
        enableReinitialize
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({ setFieldValue, values, submitCount }) => (
          <Form
            className="p-[20px_20px] lg:p-[28px_28px] xl:p-[32px_32px] h-full items-stretch"
            aria-label="Request for Proposal Form"
          >
            <h5 className="xl:text-[24px] xl:leading-[31.2px] text-[20px] leading-[26px] font-[700] text-[#040405]  xl:mb-[24px] mb-[16px]">
              Please Enter Your Details
            </h5>

            <div>
              <div className="grid lg:grid-cols-2 gap-4 h-full items-stretch">
                <FormInput
                  required
                  type="text"
                  name="first_name"
                  placeholder="First name"
                />
                <FormInput name="last_name" placeholder="Last name" required />
                <FormInput
                  required
                  type="tel"
                  name="contact_no"
                  placeholder="Phone number"
                />
                <FormInput
                  required
                  name="email"
                  type="email"
                  placeholder="Email"
                />
                <div>
                  <ServiceSelect
                    disabled
                    title="Position Applying For*"
                    selectedServices={[role]}
                    options={data?.map((item: any) => item.position)}
                    onChange={(services: string[]) => {
                      setRole(services.toString());
                      setFieldValue("job_role", services.toString());
                    }}
                  />
                </div>
                <div>
                  <ServiceSelect
                    title="Total Experience*"
                    selectedServices={[values.total_experience]}
                    onChange={(services: string[]) =>
                      setFieldValue("total_experience", services.toString())
                    }
                    options={["Fresher", ...years]}
                  />
                </div>
                <FormInput
                  name="current_job_role"
                  placeholder="Current Job Title"
                />
                <FormInput
                  name="current_company"
                  placeholder="Current Company"
                />
              </div>
              <div className="flex flex-col h-full mt-[16px]">
                <FileUploader
                  file={selectedFiles[0]}
                  onFileChange={(val) => {
                    setSelectedFiles([val]);
                    setFieldValue("resume", val);
                  }}
                />
                {submitCount > 0 && (
                  <ErrorMessage
                    name="resume"
                    id="resume-error"
                    component={ErrorMessageComponent}
                  />
                )}
              </div>
            </div>

            <div className="flex items-center md:justify-between justify-end md:flex-nowrap flex-wrap gap-6 mt-6">
              <div className="flex items-center gap-8 md:w-auto w-full"></div>
              <Button
                type="submit"
                variant="small"
                loading={isLoading}
                className="my-0 sm:w-fit w-full"
                label={data?.button_text || "Submit"}
                onClick={() =>
                  console.log(
                    "[ApplyForm] Submit button clicked (native click event)",
                  )
                }
              />
            </div>
          </Form>
        )}
      </Formik>
      <div className="hidden">
        {sitekey && (
          <ReCAPTCHA
            style={{ display: "inline" }}
            theme="dark"
            size="invisible"
            ref={reCaptchaRef}
            sitekey={sitekey}
          />
        )}
      </div>
    </div>
  );
};
export default ApplyForm;
